import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ADMISSION_TYPE_BOTTLE_SERVICE,
  ADMISSION_TYPE_GUEST_LIST,
  ADMISSION_TYPE_TICKET,
  AdmissionType,
} from '@domain/admission';
import {
  BasePreset,
  BottleServicePreset,
  CreateAdmissionPresetRequest,
  Preset,
  PresetsRequest,
  TicketPreset,
} from '@domain/preset';
import { useGenericMutation } from '@hooks/use-generic-mutation';
import { useToast } from '@hooks/use-toast/use-toast';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { request } from '@utils/request/request';
import { RequestMethod } from '@utils/request/request.type';

import { QUERY_KEYS } from './constants';

interface VariablesType {
  preset: CreateAdmissionPresetRequest;
  type: AdmissionType;
}

interface Props {
  floorplanId?: string;
  setCurrentFloorplanAdmissions?: Dispatch<
    SetStateAction<
      | {
          bottleService: BottleServicePreset[];
          guestList: BasePreset[];
          ticket: TicketPreset[];
        }
      | undefined
    >
  >;
}

const updateQueryState = (queryClient: QueryClient, queryKey: string[], data: Preset, variables: VariablesType) => {
  const previousPresets = queryClient.getQueryData<PresetsRequest>(queryKey);

  if (previousPresets) {
    queryClient.setQueryData(queryKey, {
      ...previousPresets,
      [variables.type]: [...previousPresets[variables.type], data],
    });
  } else {
    const allData = {
      [ADMISSION_TYPE_BOTTLE_SERVICE]: [],
      [ADMISSION_TYPE_GUEST_LIST]: [],
      [ADMISSION_TYPE_TICKET]: [],
    };

    queryClient.setQueryData(queryKey, {
      ...allData,
      [variables.type]: [data],
    });
  }
};

const createPreset = async (preset: CreateAdmissionPresetRequest, type: AdmissionType) => {
  const urlType = {
    [ADMISSION_TYPE_GUEST_LIST]: 'guest-list-presets',
    [ADMISSION_TYPE_BOTTLE_SERVICE]: 'bottle-service-presets',
    [ADMISSION_TYPE_TICKET]: 'ticket-presets',
  };

  const url = `${import.meta.env.VITE_OFFERS_HOST}/v1/nightgraph/${urlType[type]}`;

  return request({ url, method: RequestMethod.POST, body: preset });
};

export const useCreateAdmissionPreset = ({ floorplanId = '' }: Props) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const toast = useToast();

  return useGenericMutation(
    [QUERY_KEYS.createAdmissionPreset],
    async ({ preset, type }: { preset: CreateAdmissionPresetRequest; type: AdmissionType }) =>
      createPreset(preset, type),
    {
      onSuccess: (data: Preset, variables) => {
        updateQueryState(queryClient, [QUERY_KEYS.getAdmissionsPresets], data, variables);
        updateQueryState(queryClient, ['get-floorplan-preset-admissions', floorplanId], data, variables);

        toast.push({
          title: t('offers:createOfferSuccessNotificationTitle'),
          text: '',
          type: 'success',
        });
      },
    }
  );
};
